import * as THREE from '@phdsoft/three';
import { RepairGeometryUtils } from './repair-geometry-utils';

export interface RoundedCornerParams {
    offsRadiusInRect: THREE.Vector3[][];
    rectangleCutCorners: THREE.Line3[];
    radiusStartEnd: number[][];
    offsHoriz: THREE.Vector3;
    offsHorizNeg: THREE.Vector3;
    offsVert: THREE.Vector3;
    offsVertNeg: THREE.Vector3;
}

export class RepairRoundedCorners {
    public params: RoundedCornerParams;

    constructor() {
        this.params = {
            offsRadiusInRect: [],
            rectangleCutCorners: [],
            radiusStartEnd: [],
            offsHoriz: undefined,
            offsHorizNeg: undefined,
            offsVert: undefined,
            offsVertNeg: undefined,
        };
    }

    public init(
        rectangleCorners: THREE.Vector3[],
        sizeScalar: number,
        radiusRatio: number = 0.002,
        absoluteRadius: number | number[] = undefined
    ) {
        if (rectangleCorners.length < 4) return;

        this.params.offsRadiusInRect = [];
        this.params.rectangleCutCorners = [];

        for (let i = 0; i < 4; i++) {
            let r = 0;
            if (Array.isArray(absoluteRadius)) {
                // Use the value from array if it exists and is >= 0
                r = (absoluteRadius[i] !== undefined && absoluteRadius[i] !== null)
                    ? absoluteRadius[i]
                    : radiusRatio * sizeScalar;
            } else if (absoluteRadius !== undefined && absoluteRadius !== null && absoluteRadius > 0) {
                r = absoluteRadius;
            } else {
                r = radiusRatio * sizeScalar;
            }

            const c = rectangleCorners[i];
            const c_next = rectangleCorners[(i + 1) % 4];
            const c_prev = rectangleCorners[(i + 3) % 4];

            const vForw = c_next.clone().sub(c).normalize().multiplyScalar(r);
            const vBack = c_prev.clone().sub(c).normalize().multiplyScalar(r);

            this.params.offsRadiusInRect.push([vForw, vBack]);

            const p1 = c.clone().add(vForw);
            const p2 = c.clone().add(vBack);
            this.params.rectangleCutCorners.push(new THREE.Line3(p1, p2));
        }

        // Populate legacy fields with dummy values just in case (e.g. average from corner 0)
        // though strictly they shouldn't be used if we rely on offsRadiusInRect
        if (this.params.offsRadiusInRect.length > 0) {
            this.params.offsHoriz = this.params.offsRadiusInRect[0][0];
            this.params.offsVert = this.params.offsRadiusInRect[0][1];
            this.params.offsHorizNeg = this.params.offsHoriz.clone().negate();
            this.params.offsVertNeg = this.params.offsVert.clone().negate();
        }

        const a0 = 0, a1 = Math.PI / 2, a2 = Math.PI, a3 = (Math.PI * 3) / 2, a4 = Math.PI * 2;
        this.params.radiusStartEnd = [
            [a2, a3],
            [a3, a4],
            [a0, a1],
            [a1, a2],
        ];
    }

    public updateListCornersInPlate(
        listCornersInPlate: number[],
        rectangleCorners: THREE.Vector3[],
        tri: THREE.Triangle
    ): void {
        for (let indCorner = 0; indCorner < rectangleCorners.length; indCorner++) {
            let a = rectangleCorners[indCorner];
            let b = this.params.rectangleCutCorners[indCorner].start;
            let c = this.params.rectangleCutCorners[indCorner].end;

            let vert1 = tri.containsPoint(a) ? 1 : 0;
            let vert2 = tri.containsPoint(b) ? 2 : 0;
            let vert3 = tri.containsPoint(c) ? 4 : 0;

            listCornersInPlate[indCorner] |= vert1 | vert2 | vert3;
        }
    }

    public intersectTriangleEdgesWithCuts(
        edges: THREE.Line3[],
        listCutCornersIntersected: THREE.Vector3[][]
    ): void {
        for (let edgeCorner of this.params.rectangleCutCorners) {
            let sublistIntersected: THREE.Vector3[] = [];
            for (let edgeTri of edges) {
                let intersectCutCorner = new THREE.Line3();
                if (RepairGeometryUtils.vertIntersectLines3D(edgeTri, edgeCorner, intersectCutCorner)) {
                    sublistIntersected.push(intersectCutCorner.start);
                }
            }
            listCutCornersIntersected.push(sublistIntersected);
        }
    }

    public getCornerCenter(
        cornerIndex: number,
        cornerPoint: THREE.Vector3
    ): THREE.Vector3 | null {
        if (!this.params.offsRadiusInRect[cornerIndex]) return null;

        const off1 = this.params.offsRadiusInRect[cornerIndex][0];
        const off2 = this.params.offsRadiusInRect[cornerIndex][1];

        return cornerPoint.clone().add(off1).add(off2);
    }

    public getCornerArcPoints(
        cornerIndex: number,
        cornerPoint: THREE.Vector3,
        segments: number = 8
    ): THREE.Vector3[] {
        if (!this.params.offsRadiusInRect[cornerIndex]) return [];

        const off1 = this.params.offsRadiusInRect[cornerIndex][0];
        const off2 = this.params.offsRadiusInRect[cornerIndex][1];

        // Center of the rounding circle
        const center = cornerPoint.clone().add(off1).add(off2);

        const vStart = cornerPoint.clone().add(off1).sub(center);
        const vEnd = cornerPoint.clone().add(off2).sub(center);
        const radius = vStart.length();

        const points: THREE.Vector3[] = [];

        for (let i = 1; i < segments; i++) {
            const t = i / segments;
            const v = vStart.clone().lerp(vEnd, t).normalize().multiplyScalar(radius);
            points.push(center.clone().add(v));
        }

        return points;
    }
}
